/* eslint-disable max-lines-per-function */
import { ReactComponent as MenuIcon } from '@paradigm/icons/src/mailru/navigation/menu.svg';
import { ReactComponent as MoreIcon } from '@paradigm/icons/src/mailru/navigation/more_vertical.svg';
import { Icon20Cancel, Icon20Rectangle2HorizontalOutline, Icon20Search, Icon20Square4Outline } from '@vkontakte/icons';
import api from 'Cloud/Application/api';
import config from 'Cloud/config';
import classNames from 'clsx';
import throttle from 'lodash.throttle';
import React, { memo, ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { HIDE_ADS } from 'reactApp/appHelpers/configHelpers';
import { isTouchSearchEnabled } from 'reactApp/appHelpers/featuresHelpers';
import { ROOT_FOLDER_ID } from 'reactApp/constants/magicIdentificators';
import { useFallbackSearchUrl } from 'reactApp/hooks/useFallbackSearchUrl';
import { isDefaultRootContent } from 'reactApp/modules/home/home.selectors';
import { historyPush } from 'reactApp/modules/router/router.module';
import { getCurrentStorage } from 'reactApp/modules/router/router.selectors';
import { getSearchRequestParams } from 'reactApp/modules/search/search.selectors';
import { resetSelect, startSelecting, toggleAll } from 'reactApp/modules/selections/selections.actions';
import { SelectionsSelectors } from 'reactApp/modules/selections/selections.selectors';
import { setViewMode } from 'reactApp/modules/settings/settings.module';
import { SettingsSelectors } from 'reactApp/modules/settings/settings.selectors';
import { EViewMode } from 'reactApp/modules/settings/settings.types';
import { getIdByStorage } from 'reactApp/modules/storage/storage.helpers';
import { getIds, getStorageCurrentFolder, isEmptyFolder } from 'reactApp/modules/storage/storage.selectors';
import { EStorageType } from 'reactApp/modules/storage/storage.types';
import { setShowUploaderAction } from 'reactApp/modules/uploadList/uploadList.module';
import { UserSelectors } from 'reactApp/modules/user/user.selectors';
import { RootState } from 'reactApp/store';
import { Button } from 'reactApp/ui/Button/Button';
import { renderMobileLeftMenu } from 'reactApp/ui/Mobile/LeftMenu/LeftMenu.helpers';
import { Logo } from 'reactApp/ui/Mobile/Logo/Logo';
import { renderMobileOptionsModal } from 'reactApp/ui/Mobile/OptionsModal/OptionsModal.helpers';
import { renderSubscriptionsModal } from 'reactApp/ui/Mobile/SubscriptionsModal/SubscriptionsModal.helpers';
import { sendDwh } from 'reactApp/utils/ga';
import { ECategoryGa, sendPaymentGa } from 'reactApp/utils/paymentGa';

import styles from './Toolbar.css';

const apiVersion = api.getOptions().version;
const buildVersion = config.get('BUILD');

const TOP_PADDING = HIDE_ADS ? 10 : 98;

interface IProps {
    disableActions?: boolean;
    showSelection?: boolean;
    showViewChange?: boolean;
    showOptions?: boolean;
    showSearch?: boolean;
    hideLogo?: boolean;
    showBack?: boolean;
    showMenu?: boolean;
    logoWithEndorsment?: boolean;
    showSubscriptions?: boolean;
}

export const Toolbar = memo(
    ({
        disableActions = false,
        showMenu = false,
        logoWithEndorsment = true,
        showSelection = true,
        showViewChange = false,
        showSearch = true,
        hideLogo = false,
        showBack = false,
        showOptions = true,
        showSubscriptions = false,
    }: IProps): ReactElement => {
        const dispatch = useDispatch();

        const storage = useSelector(getCurrentStorage);
        const folder = useSelector((state: RootState) => getStorageCurrentFolder(state, storage));

        const ids = useSelector((state: RootState) => getIds(state, storage));
        const selectedIds = useSelector(SelectionsSelectors.getSelectedIdxs);
        const isAllSelected = ids.length === selectedIds.length;
        const isSelecting = useSelector(SelectionsSelectors.isSelecting);
        const isDefaultContent = useSelector(isDefaultRootContent);
        const isEmpty = useSelector(isEmptyFolder);
        const isEmptyContent = isDefaultContent || isEmpty;
        const viewMode = useSelector((state) => SettingsSelectors.getViewByStorage(state, storage));
        const isList = viewMode === EViewMode.list;
        const { query, xPageId, xReqId } = useSelector(getSearchRequestParams);
        const isProUser = useSelector(UserSelectors.isProUser);
        const isPaidUser = useSelector(UserSelectors.isPaidUser);
        const cancelSearchFallbackPath = useFallbackSearchUrl(getIdByStorage(storage));

        const openOptions = useCallback(() => {
            if (folder) {
                dispatch(setShowUploaderAction(false));
                renderMobileOptionsModal({ id: folder?.id });
            }
        }, [dispatch, folder]);

        const [isSticky, setSticky] = useState<boolean>(false);

        const onScroll = useCallback(() => setSticky(window.scrollY > TOP_PADDING), []);

        const throttledScroll = throttle(onScroll, 100);

        const onToggleView = useCallback(() => {
            const type = isList ? EViewMode.thumbs : EViewMode.list;
            dispatch(setViewMode({ viewMode: type, storage }));
        }, [isList, dispatch, storage]);

        const onStartSelecting = useCallback(() => {
            dispatch(setShowUploaderAction(false));
            dispatch(startSelecting());
        }, [dispatch]);

        const onResetSelections = useCallback(() => dispatch(resetSelect()), [dispatch]);

        const onSelectAll = useCallback(() => {
            if (!isAllSelected) {
                sendPaymentGa({
                    eventCategory: ECategoryGa.toolbar,
                    action: 'select_all',
                    source: storage,
                });
            }
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            dispatch(toggleAll({ allIdxs: ids, storage }));
        }, [dispatch, ids, storage]);

        const onSearchClick = useCallback(() => {
            dispatch(historyPush({ id: getIdByStorage(EStorageType.search) }));
            sendDwh({
                eventCategory: ECategoryGa.search,
                action: 'initial_click',
                dwhData: {
                    page_id: xPageId,
                    req_id: xReqId,
                    q: query,
                    path: ROOT_FOLDER_ID,
                    section: storage,
                    api: apiVersion,
                    build: buildVersion,
                    free_user: !isPaidUser,
                    paid_user: isPaidUser,
                    pro_user: isProUser,
                },
            });
        }, [dispatch]);

        const onCloseSearch = useCallback(() => {
            const { search, pathname } = cancelSearchFallbackPath.current;

            dispatch(historyPush({ id: pathname, search }));
        }, [dispatch]);

        useEffect(() => {
            onResetSelections();
        }, [folder?.id, onResetSelections]);

        useEffect(() => {
            document.addEventListener('scroll', throttledScroll);

            return () => document.removeEventListener('scroll', throttledScroll);
        }, [throttledScroll]);

        const left = useMemo(
            () =>
                isSelecting ? (
                    <div className={styles.button} onClick={onSelectAll} data-qa-button={isAllSelected ? 'cancelSelect' : 'selectAll'}>
                        <div>{isAllSelected ? 'Отменить выбор' : 'Выбрать все'}</div>
                    </div>
                ) : (
                    <>
                        {showBack && (
                            <div className={classNames(styles.button, styles.button_square)} onClick={onCloseSearch}>
                                <Icon20Cancel />
                            </div>
                        )}

                        {showMenu && (
                            <div className={styles.menu} onClick={renderMobileLeftMenu} data-qa-button={'menu'}>
                                <MenuIcon width={16} height={16} fill="currentColor" />
                            </div>
                        )}

                        {!hideLogo && <Logo withEndorsment={logoWithEndorsment} />}

                        {showSubscriptions && (
                            <Button className={styles.button} onClick={renderSubscriptionsModal}>
                                Мои подписки
                            </Button>
                        )}
                    </>
                ),
            [isSelecting, onSelectAll, isAllSelected, showMenu, logoWithEndorsment, showSubscriptions]
        );

        const right = useMemo(
            () =>
                isSelecting ? (
                    <div className={styles.button} onClick={onResetSelections} data-qa-button={'cancel'}>
                        <div>Отменить</div>
                    </div>
                ) : (
                    <>
                        {showSelection && (
                            <div className={styles.button} onClick={onStartSelecting} data-qa-button={'select'}>
                                <div>Выбрать</div>
                            </div>
                        )}
                        {showViewChange && isTouchSearchEnabled && (
                            <div
                                className={classNames(styles.button, styles.button_square)}
                                data-qa-button={'change-view'}
                                onClick={onToggleView}
                            >
                                {isList ? <Icon20Rectangle2HorizontalOutline /> : <Icon20Square4Outline />}
                            </div>
                        )}
                        {showSearch && isTouchSearchEnabled && (
                            <div className={classNames(styles.button, styles.button_square)} onClick={onSearchClick}>
                                <Icon20Search />
                            </div>
                        )}
                        {showOptions && (
                            <div className={classNames(styles.button, styles.button_icon)} onClick={openOptions} data-qa-button={'options'}>
                                <MoreIcon width={20} height={20} fill="#currentColor" />
                            </div>
                        )}
                    </>
                ),
            [isList, isSelecting, onResetSelections, onStartSelecting, onToggleView, openOptions, showOptions]
        );

        return (
            <div
                className={classNames(styles.root, {
                    [styles.root_sticky]: isSticky,
                })}
            >
                <div className={styles.left}>{left}</div>
                {!isEmptyContent && !disableActions && <div className={styles.right}>{right}</div>}
            </div>
        );
    }
);

Toolbar.displayName = 'Toolbar';
